import { useEffect, useRef } from "react";
import {
  Drawer,
  IconButton,
  Typography,
  Box,
  Stack,
} from "@mui/material";
import { Error as ErrorIcon } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import FlexBox from "../../layouts/flexbox";
import ZoomPlayer from "./ZoomPlayer";

type Party = {
  firstName?: string;
  lastName?: string;
  title?: string;
  company?: string;
};

interface ZoomMeetingModalProps {
  open: boolean;
  videoUrl: string;
  vttUrl?: string;
  downloadUrls?: Record<string, string>;
  summaryData?: Record<string, string[]> | null;
  userData?: { ambassador?: Party; client?: Party };
  onClose: () => void;
  title?: string;
}

function drawerTitle(userData?: { ambassador?: Party; client?: Party }) {
  return `Virtual Meeting (Facilitated by Execweb) - ${userData?.ambassador?.firstName ?? ""}, ${userData?.ambassador?.title ?? ""}  @ ${userData?.ambassador?.company ?? ""}`;
}

export default function ZoomMeetingModal({
  open,
  videoUrl,
  vttUrl = "",
  downloadUrls = {},
  summaryData,
  userData,
  onClose,
  title,
}: ZoomMeetingModalProps) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const playerTitle =
    title ?? (userData ? drawerTitle(userData) : undefined);

  const handleClose = () => {
    if (iframeRef.current) {
      iframeRef.current.src = "";
    }
    onClose();
  };

  useEffect(() => {
    return () => {
      if (iframeRef.current) {
        iframeRef.current.src = "";
      }
    };
  }, []);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: [
          {
            width: { xs: "100%", md: "80%", xl: "70%" },
            maxWidth: "100%",
            height: "100%",
            maxHeight: "100vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            overscrollBehavior: "contain",
          },
        ],
      }}
    >
      <Stack sx={{ flex: 1, minHeight: 0, height: "100%", overflow: "hidden" }}>
        <FlexBox
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: 1,
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Typography variant="h6" component="span">
            Play recording
          </Typography>
          <IconButton onClick={handleClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </FlexBox>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            p: 2,
            pb: 2,
          }}
        >
          {!videoUrl ? (
            <FlexBox
              pb={2}
              justifyContent="center"
              alignItems="center"
              gap={0.5}
              sx={{ minHeight: 400 }}
            >
              <ErrorIcon fontSize="medium" color="error" />
              <Typography component="span" color="error" pt={0.5}>
                Failed to load the recording. Please try again later.
              </Typography>
            </FlexBox>
          ) : videoUrl.includes("zoom.us") ? (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                minHeight: { xs: 360, md: "calc(100vh - 100px)" },
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <iframe
                ref={iframeRef}
                src={videoUrl}
                title="Zoom Recording"
                style={{ width: "100%", height: "100%", border: 0, minHeight: 420 }}
                allow="autoplay; fullscreen; picture-in-picture"
              />
            </Box>
          ) : (
            <Box sx={{ width: "100%", height: "100%", overflow: "auto" }}>
              <ZoomPlayer
                title={playerTitle}
                videoUrl={videoUrl}
                vttUrl={vttUrl}
                downloadUrls={downloadUrls}
                summaryData={summaryData ?? null}
              />
            </Box>
          )}
        </Box>
      </Stack>
    </Drawer>
  );
}
